import React, { useEffect, useState } from "react";
import axios from "axios";
import ipconfig from "../../Core/ipconfig";
import {
  Box,
  Typography,
  Chip,
  Button,
  Grid,
  Card,
  CardContent,
  Divider,
  useTheme,
  Avatar,
} from "@mui/material";
import { toast } from "react-toastify";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import Spinner from "../../Shared/Feature/LoadingSpinner/Spinner";

const InfoRow = ({ label, value }: { label: string; value?: any }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      py: 1,
      borderBottom: "1px solid",
      borderColor: "divider",
      gap: 2,
    }}
  >
    <Typography variant="body2" sx={{ color: "text.secondary", fontWeight: 500 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 600, color: "text.primary", textAlign: "right" }}>
      {value !== undefined && value !== null && value !== "" ? value : "—"}
    </Typography>
  </Box>
);

export default function PartnersDetail({ data, onEditSuccess }: any) {
  const theme = useTheme();
  const [partner, setPartner] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  const fetchPartner = async () => {
    setFetching(true);
    try {
      const res = await axios.get(
        `${ipconfig}/fetch/partner/details?Id=${data?._id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setPartner(res.data.data);
    } catch (err) {
      console.error(err);
    } finally {
      setFetching(false);
    }
  };

  const updateApproval = async (approvedStatus: boolean) => {
    try {
      setLoading(true);
      await axios.put(
        `${ipconfig}/update/partner?Id=${partner._id}`,
        { approvedStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      toast.success(
        approvedStatus ? "Partner approved successfully" : "Partner approval status revoked"
      );
      if (onEditSuccess) onEditSuccess();
      fetchPartner();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Action failed");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (data?._id) {
      fetchPartner();
    }
  }, [data]);

  if (fetching || !partner) {
    return <Spinner height="100%" />;
  }

  return (
    <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: "24px" }}>
      {/* Top Banner / Approval controls */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "16px" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <Avatar sx={{ bgcolor: "primary.main", width: "44px", height: "44px" }}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 750, color: "text.primary" }}>
              {partner.contactPersonName}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              Code: {partner.partnerCode || "N/A"} • Type: {partner.partnerType || "N/A"}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: "12px" }}>
          {!partner.approvedStatus ? (
            <Button
              variant="contained"
              color="success"
              disabled={loading}
              onClick={() => updateApproval(true)}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Approve Partner
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="error"
              disabled={loading}
              onClick={() => updateApproval(false)}
              sx={{ textTransform: "none", fontWeight: 600 }}
            >
              Revoke Approval
            </Button>
          )}
        </Box>
      </Box>

      <Divider />

      <Grid container spacing={3}>
        {/* Personal Details */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: "12px", height: "100%" }}>
            <CardContent sx={{ p: "20px !important" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 750, mb: 2, color: "primary.main", display: "flex", alignItems: "center", gap: "8px" }}>
                <PersonIcon sx={{ fontSize: 20 }} />
                Personal Details
              </Typography>
              <InfoRow label="Contact Person" value={partner.contactPersonName} />
              <InfoRow label="Phone Number" value={partner.contactNumber} />
              <InfoRow label="Email Address" value={partner.emailId} />
              <InfoRow label="Partner Type" value={partner.partnerType} />
              <InfoRow label="Partner Code" value={partner.partnerCode} />
            </CardContent>
          </Card>
        </Grid>

        {/* Company Profile or Individual Profile */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ borderRadius: "12px", height: "100%" }}>
            <CardContent sx={{ p: "20px !important" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 750, mb: 2, color: "primary.main", display: "flex", alignItems: "center", gap: "8px" }}>
                <BusinessIcon sx={{ fontSize: 20 }} />
                {partner.partnerType === "Company" ? "Company Profile" : "Profile Details"}
              </Typography>
              {partner.partnerType === "Company" ? (
                <>
                  <InfoRow label="Legal Company Name" value={partner.companyProfile?.legalCompanyName} />
                  <InfoRow label="Nature of Business" value={partner.companyProfile?.natureOfBusiness} />
                  <InfoRow label="City" value={partner.companyProfile?.city} />
                  <InfoRow label="State" value={partner.companyProfile?.state} />
                  <InfoRow label="Pin Code" value={partner.companyProfile?.pinCode} />
                </>
              ) : (
                <>
                  <InfoRow label="Full Name" value={partner.contactPersonName} />
                  <InfoRow label="City" value={partner.individualProfile?.city} />
                  <InfoRow label="State" value={partner.individualProfile?.state} />
                  <InfoRow label="Pin Code" value={partner.individualProfile?.pinCode} />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Financial & Compliance Info */}
        <Grid item xs={12}>
          <Card variant="outlined" sx={{ borderRadius: "12px" }}>
            <CardContent sx={{ p: "20px !important" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 750, mb: 2, color: "primary.main", display: "flex", alignItems: "center", gap: "8px" }}>
                <AccountBalanceIcon sx={{ fontSize: 20 }} />
                Compliance & Banking Details
              </Typography>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, display: "block", mb: 1 }}>
                    TAX & IDENTIFICATION
                  </Typography>
                  {partner.partnerType === "Company" ? (
                    <>
                      <InfoRow label="PAN Card Number" value={partner.companyProfile?.panCardNumber} />
                      <InfoRow label="GST Number" value={partner.companyProfile?.gstNumber} />
                    </>
                  ) : (
                    <>
                      <InfoRow label="PAN Card Number" value={partner.individualProfile?.panCardNumber} />
                      <InfoRow label="GST Number" value={partner.individualProfile?.gstNumber || "N/A"} />
                    </>
                  )}
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700, display: "block", mb: 1 }}>
                    BANK ACCOUNT
                  </Typography>
                  <InfoRow label="Account Holder" value={partner.bankDetails?.bankAccountName} />
                  <InfoRow label="Account Number" value={partner.bankDetails?.bankAccountNumber} />
                  <InfoRow label="Bank Name" value={partner.bankDetails?.bankName} />
                  <InfoRow label="IFSC Code" value={partner.bankDetails?.bankIfscCode} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}